import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader } from "@/components/common/loader";
import { AddRequestDialog } from "@/features/eservices/components/add-request-dialog";
import {
  deleteRequests,
  getStatus,
  listRequests,
  requeueRequests,
} from "@/features/eservices/api";
import type { RequestStatus, TranscriptRequest } from "@/features/eservices/types";
import { useTranscriptsContext } from "./transcripts-layout";

const STATUS_VARIANT: Record<RequestStatus, "outline" | "secondary" | "default" | "destructive"> = {
  NOT_REQUESTED: "outline",
  QUEUED: "secondary",
  OK: "default",
  ERROR: "destructive",
};

const STATUS_LABEL: Record<RequestStatus, string> = {
  NOT_REQUESTED: "Not requested",
  QUEUED: "Queued",
  OK: "OK",
  ERROR: "Error",
};

export function TranscriptRequestsPage() {
  const { client } = useTranscriptsContext();
  const [rows, setRows] = useState<TranscriptRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [down, setDown] = useState<{ down: boolean; message: string }>({
    down: false,
    message: "",
  });

  const refresh = useCallback(() => {
    setLoading(true);
    listRequests(client.id)
      .then(setRows)
      .catch(() => toast.error("Could not load requests."))
      .finally(() => setLoading(false));
  }, [client.id]);

  useEffect(refresh, [refresh]);
  useEffect(() => {
    getStatus()
      .then((s) => setDown({ down: s.eservicesDown, message: s.eservicesMessage }))
      .catch(() => {});
  }, []);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const requeue = async (ids: string[]) => {
    if (ids.length === 0) return;
    try {
      await requeueRequests(client.id, ids);
      toast.success("Queued — the connector will pull these.");
      setSelected(new Set());
      refresh();
    } catch {
      toast.error("Requeue failed.");
    }
  };

  const remove = async (ids: string[]) => {
    if (ids.length === 0) return;
    try {
      await deleteRequests(client.id, ids);
      setSelected(new Set());
      refresh();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {down.down && (
        <Alert variant="destructive">
          <AlertTitle>e-Services is unavailable</AlertTitle>
          <AlertDescription>
            {down.message || "New pulls are paused until e-Services is back."}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-foreground text-sm font-semibold">
          Transcript Requests
        </h3>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={selected.size === 0}
            onClick={() => requeue([...selected])}
          >
            Pull now ({selected.size})
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={selected.size === 0}
            onClick={() => remove([...selected])}
          >
            Delete
          </Button>
          <Button size="sm" onClick={() => setAddOpen(true)} disabled={down.down}>
            Add request
          </Button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">No transcript requests yet.</p>
      ) : (
        <Card className="gap-0 overflow-hidden p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" />
                <TableHead>Type</TableHead>
                <TableHead>For</TableHead>
                <TableHead>Year / Period</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Check</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Checkbox
                      checked={selected.has(r.id)}
                      onCheckedChange={() => toggle(r.id)}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{r.transType}</TableCell>
                  <TableCell className="text-muted-foreground">{r.forWho}</TableCell>
                  <TableCell>
                    {r.taxYear}
                    {r.taxPeriod ? ` ${r.taxPeriod}` : ""}
                  </TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANT[r.status]}>
                      {STATUS_LABEL[r.status]}
                    </Badge>
                    {r.statusMessage && (
                      <span className="text-muted-foreground ml-2 text-xs">
                        {r.statusMessage}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {r.lastCheck ? new Date(r.lastCheck).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => requeue([r.id])}>
                      Requeue
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove([r.id])}>
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <AddRequestDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        clientId={client.id}
        isBusiness={client.type === "Business"}
        hasSpouse={Boolean(client.spouse)}
        onAdded={refresh}
      />
    </div>
  );
}
